import React, { useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Container,
  CircularProgress,
  Card,
  CardContent,
  Fade,
  Snackbar,
  Alert,
  Backdrop,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import CryptoJS from 'crypto-js';
import LanguageSwitcher from '../components/LanguageSwitcher';
import i18n from '../i18n';

const RECAPTCHA_SITE_KEY = '6LfYORkrAAAAABXYjvDkwiKj1NElIeDyxNz6hiuV';
const ENCRYPTION_KEY = 'dadash123@@123##';

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [errors, setErrors] = useState({ username: '', password: '', captcha: '' });
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const validateForm = () => {
    const newErrors = { username: '', password: '', captcha: '' };
    let isValid = true;

    if (!username.trim()) {
      newErrors.username = t('login.required') || 'Username is required';
      isValid = false;
    }
    if (!password.trim()) {
      newErrors.password = t('login.required') || 'Password is required';
      isValid = false;
    }
    if (!captchaToken) {
      newErrors.captcha = t('login.captchaRequired') || 'Please complete the CAPTCHA';
      isValid = false;
    }

    setErrors(newErrors);
    if (!isValid) {
      const firstError =
        newErrors.username ||
        newErrors.password ||
        newErrors.captcha;
      setSnackbarMessage(firstError);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    return isValid;
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await axios.post(
        'https://copyapi.avangar.ir/api/forex/UserPassLogin',
        {
          UserName: username,
          Pass: password,
          CaptchaToken: captchaToken,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        }
      );

      // لاگ کردن پاسخ API برای دیباگ
      // console.log('Login API Response:', response.data);

      // بررسی و اصلاح ParentID
      let parentID = response.data.ParentID;
      if (parentID === "00000000-0000-0000-0000-000000000000") {
        // console.log('ParentID is the default empty UUID, setting to null');
        parentID = null;
      } else if (parentID === response.data.UserID) {
        console.warn('ParentID is the same as UserID, setting to null to avoid confusion');
        parentID = null;
      }

      const userData = {
        UserID: response.data.UserID,
        Name: response.data.Name,
        Family: response.data.Family,
        Kind: response.data.Kind,
        NationalCode: response.data.NationalCode,
        MobileNO: response.data.MobileNO,
        StartDate: response.data.StartDate,
        EndDate: response.data.EndDate,
        ParentID: parentID, // استفاده از مقدار اصلاح‌شده
      };
      const encryptedData = CryptoJS.AES.encrypt(
        JSON.stringify(userData),
        ENCRYPTION_KEY
      ).toString();
      localStorage.setItem('user', encryptedData);

      setSnackbarMessage(t('login.success') || 'Login successful');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setUsername('');
      setPassword('');
      setCaptchaToken(null);
      setErrors({ username: '', password: '', captcha: '' });
      setTimeout(() => navigate('/home'), 1000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.Message ||
        (err.request ? t('login.networkError') || 'Network error' : t('login.error') || 'Login failed');
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    if (token) {
      setErrors((prev) => ({ ...prev, captcha: '' }));
    }
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(0, 255, 0, 0.3) 0%, transparent 70%)',
          animation: 'pulse 6s infinite ease-in-out',
        },
        '&:after': {
          content: '""',
          position: 'absolute',
          bottom: '20%',
          right: '10%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255, 0, 0, 0.3) 0%, transparent 70%)',
          animation: 'pulse 6s infinite ease-in-out reverse',
        },
      }}
    >
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, bgcolor: 'rgba(0,0,0,0.5)' }}
        open={loading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: 'transparent',
            background: 'linear-gradient(90deg, #00ff00, #ff0000)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
      </Backdrop>
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 0.5; }
            50% { transform: scale(1.2); opacity: 0.3; }
            100% { transform: scale(1); opacity: 0.5; }
          }
          @keyframes slideInLeft {
            0% { transform: translateX(-100px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes vibrate {
            0% { transform: translateX(0); }
            20% { transform: translateX(-2px); }
            40% { transform: translateX(2px); }
            60% { transform: translateX(-2px); }
            80% { transform: translateX(2px); }
            100% { transform: translateX(0); }
          }
          @keyframes neonGlowGreen {
            0% { box-shadow: 0 0 5px rgba(0, 255, 0, 0.3); }
            50% { box-shadow: 0 0 20px rgba(0, 255, 0, 0.7); }
            100% { box-shadow: 0 0 5px rgba(0, 255, 0, 0.3); }
          }
          @keyframes softBounce {
            0% { transform: scale(1); }
            50% { transform: scale(0.95); }
            100% { transform: scale(1); }
          }
          @keyframes typing {
            0% { width: 0; }
            100% { width: 100%; }
          }
          @keyframes glow {
            0% { text-shadow: 0 0 5px rgba(0, 255, 0, 0.3), 0 0 5px rgba(255, 0, 0, 0.3); }
            50% { text-shadow: 0 0 15px rgba(0, 255, 0, 0.7), 0 0 15px rgba(255, 0, 0, 0.7); }
            100% { text-shadow: 0 0 5px rgba(0, 255, 0, 0.3), 0 0 5px rgba(255, 0, 0, 0.3); }
          }
          @keyframes fadeInDown {
            0% { transform: translateY(-20px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <Fade in timeout={600}>
        <Card
          sx={{
            maxWidth: 450,
            width: '100%',
            borderRadius: 3,
            boxShadow: `
              0 8px 24px rgba(0,0,0,0.2),
              0 0 20px rgba(0,255,0,0.2),
              0 0 20px rgba(255,0,0,0.2)
            `,
            overflow: 'hidden',
            background: 'rgba(255,255,255,0.95)',
            position: 'relative',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: `
                0 8px 20px rgba(0,0,0,0.2),
                0 0 20px rgba(0,255,0,0.4),
                0 0 20px rgba(255,0,0,0.4)
              `,
              // Removed vibrate animation to prevent jumpy effect
            },
            transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
            animation: 'slideInLeft 0.5s ease-out forwards',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              display: 'flex',
              gap: 1,
            }}
          >
            <LanguageSwitcher />
          </Box>
          <CardContent sx={{ p: { xs: 3, sm: 4 }, pt: 6 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  color: '#1e3c72',
                  mb: 3,
                  textShadow: '0 0 10px rgba(0,255,0,0.3)',
                  animation: 'typing 2s steps(30, end) forwards, glow 2s infinite',
                }}
              >
                {t('login.title') || 'Login'}
              </Typography>

              <TextField
                label={t('login.username') || 'Username'}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                fullWidth
                margin="normal"
                error={!!errors.username}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: '#2a5298' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1e3c72',
                      boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                    },
                  },
                }}
              />
              <TextField
                label={t('login.password') || 'Password'}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                marginicons
                margin="normal"
                error={!!errors.password}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: '#2a5298' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1e3c72',
                      boxShadow: '0 0 8px rgba(255,0,0,0.3)',
                    },
                  },
                }}
              />

              <Box sx={{ mt: 2, mb: 3, display: 'flex', justifyContent: 'center' }}>
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={handleCaptchaChange}
                  hl={i18n.language}
                />
              </Box>

              <Button
                variant="contained"
                onClick={handleLogin}
                fullWidth
                disabled={loading}
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: 2,
                  background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)',
                  '&:hover': {
                    background: '#1e3c72',
                    transform: 'scale(1.05)',
                    boxShadow: '0 0 12px rgba(0,255,0,0.4), 0 0 12px rgba(255,0,0,0.4)',
                    animation: 'neonGlowGreen 1s infinite',
                  },
                  '&:active': {
                    animation: 'softBounce 0.3s ease-in-out',
                  },
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : t('login.submit') || 'Login'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Fade>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            animation: 'fadeInDown 0.5s ease-out forwards',
          },
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            bgcolor: snackbarSeverity === 'success' ? '#e8f5e9' : '#ffebee',
            color: snackbarSeverity === 'success' ? '#2e7d32' : '#c62828',
            '& .MuiAlert-icon': {
              color: snackbarSeverity === 'success' ? '#4caf50' : '#d32f2f',
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Login;